import * as React from 'react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Copyright from '../internals/components/Copyright';
import StatCard from './StatCard';
import { getOrganizations, getMaps } from '../services/api';
import { useOrganizationChange } from '../contexts/GlobalContext';

// Sample data for the stats cards
const createStatData = (value, title) => [
  200, 240, 220, 260, 240, 380, 100, 240, 280, 240, 300, 340, 320, 360, 340,
  380, 360, 400, 380, 420, 400, 640, 340, 460, 440, 480, 460, 600, 880, 920,
];

export default function DeviceMappingMainGrid() {
  const [stats, setStats] = useState({
    totalMaps: 0,
    totalOrganizations: 0,
    recentMaps: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  // Reload dashboard data when organization changes
  useOrganizationChange(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [mapsData, orgsData] = await Promise.all([
        getMaps(),
        getOrganizations(),
      ]);

      setStats({
        totalMaps: Array.isArray(mapsData?.results)
          ? mapsData.results.length
          : 0,
        totalOrganizations: Array.isArray(orgsData?.results)
          ? orgsData.results.length
          : 0,
        recentMaps: Array.isArray(mapsData?.results)
          ? mapsData.results.slice(0, 5)
          : [],
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const data = [
    {
      title: 'Total Maps',
      value: String(stats.totalMaps),
      interval: 'Current count',
      trend: 'up',
      data: createStatData(stats.totalMaps, 'Maps'),
    },
    {
      title: 'Organizations',
      value: String(stats.totalOrganizations),
      interval: 'Current count',
      trend: 'neutral',
      data: createStatData(stats.totalOrganizations, 'Orgs'),
    },
    {
      title: 'Recent Maps',
      value: String(stats.recentMaps.length),
      interval: 'Last created',
      trend: 'up',
      data: createStatData(stats.recentMaps.length, 'Recent'),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
        <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
          Loading dashboard data...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      {/* Overview cards */}
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Device Mapping Overview
      </Typography>
      <Grid container spacing={2} sx={{ mb: theme => theme.spacing(2) }}>
        {data.map((card, index) => (
          <Grid key={index} item xs={12} sm={6} lg={4}>
            <StatCard {...card} />
          </Grid>
        ))}
      </Grid>

      {/* Recent Maps section */}
      {stats.recentMaps.length > 0 && (
        <>
          <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
            Recent Maps
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {stats.recentMaps.map(map => (
              <Grid key={map.id} item xs={12} sm={6} md={4}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ textAlign: 'left' }}>
                    <Typography
                      variant="h6"
                      component="div"
                      gutterBottom
                      sx={{ textAlign: 'left' }}
                    >
                      {map.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: 'left' }}
                    >
                      {map.description}
                    </Typography>
                  </CardContent>
                  <CardActions
                    sx={{ justifyContent: 'flex-start', paddingLeft: 2 }}
                  >
                    <Button
                      size="small"
                      component={Link}
                      to={`/maps/${map.id}`}
                      variant="contained"
                    >
                      View Map
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* Quick Actions */}
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Quick Actions
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card variant="outlined">
            <CardContent sx={{ textAlign: 'left' }}>
              <Typography
                variant="h6"
                component="div"
                gutterBottom
                sx={{ textAlign: 'left' }}
              >
                Manage Maps
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'left' }}
              >
                Create, edit and organize your floor plan maps
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-start', paddingLeft: 2 }}>
              <Button
                size="small"
                component={Link}
                to="/maps"
                variant="outlined"
              >
                Go to Maps
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      <Copyright sx={{ my: 4 }} />
    </Box>
  );
}
